// src/pages/PriceCheck.tsx
import { useState, type ElementType } from 'react';
import { toast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useWorkspaceStore } from '@/stores/workspace';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { staggerList, staggerItem } from '@/lib/motion';
import {
  getRecentSearches,
  pushRecentSearch,
  removeRecentSearch,
} from '@/lib/recentSearches';
import { BackButton } from '@/components/BackButton';
import { AppDrawer } from '@/components/ui/AppDrawer';

interface SearchHit {
  pid: number;
  title: string;
  price: number | null;
  currency: string;
  source: string;
  store: 'coles' | 'woolworths' | null;
  pack_size: string;
  promo_pct: number | null;
  image: string | null;
}

interface SearchResponse {
  query: string;
  hits: SearchHit[];
  from_cache: 'edge' | 'kv' | null;
  api_called: boolean;
  reason?: string;
}

interface OfferRow {
  store: 'coles' | 'woolworths' | null;
  seller_raw: string;
  price_aud: number;
  pack_size: string;
  product_title: string;
  seller_url: string | null;
  fetched_at: number;
}

interface OffersResponse {
  pid: number;
  title: string;
  offers: OfferRow[];
  from_cache: 'edge' | 'kv' | null;
  api_called: boolean;
  reason?: string;
}

const STORE_LABEL: Record<string, string> = { coles: 'Coles', woolworths: 'Woolworths' };
const STORE_BG: Record<string, string> = {
  coles: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  woolworths: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};

function StoreBadge({ store, sellerRaw }: { store: string | null; sellerRaw?: string }) {
  if (!store) {
    return (
      <span className="rounded bg-muted text-muted-foreground px-1.5 py-0.5 text-xs truncate max-w-[8rem] inline-block">
        {sellerRaw ?? 'other'}
      </span>
    );
  }
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${STORE_BG[store] ?? 'bg-muted text-muted-foreground'}`}>
      {STORE_LABEL[store] ?? store}
    </span>
  );
}

// Route every third-party image through our worker so:
//   1. Capacitor Android WebView (which ignores per-element referrerPolicy on older
//      Chrome) sees a same-origin request — no Referer leak, no 403 from gstatic.com
//   2. The worker edge-caches the response for 24h, so repeat views are essentially free
//   3. requireAuth on the worker side blocks anonymous abuse
function proxiedImg(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  return `/api/img-proxy?url=${encodeURIComponent(url)}`;
}

// Product thumbnail with graceful fallback. When the source URL is missing OR
// the image fails to load (404, CORS, blocked by mobile browser), show a neutral
// theme-aware placeholder with a shopping-cart emoji instead of an opaque dark
// square. Theme-aware `bg-muted` works in both light and dark themes; the old
// hardcoded `bg-zinc-800` rendered as a near-black square on light theme.
function ProductImage({
  src,
  size,
}: {
  src: string | null | undefined;
  size: 'sm' | 'md';
}) {
  const [failed, setFailed] = useState(false);
  const sizeClass = size === 'md' ? 'w-16 h-16' : 'w-14 h-14';

  if (!src || failed) {
    return (
      <div
        className={`${sizeClass} rounded bg-muted shrink-0 flex items-center justify-center text-muted-foreground`}
        aria-hidden
      >
        <span className="text-2xl">🛒</span>
      </div>
    );
  }

  return (
    <img
      src={proxiedImg(src)!}
      alt=""
      loading="lazy"
      // Defence-in-depth: same-origin proxy already strips Referer, but
      // referrerPolicy stays as a fallback for browsers that bypass the proxy.
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className={`${sizeClass} rounded object-cover bg-muted shrink-0`}
    />
  );
}

function RecentSearches({
  items,
  onPick,
  onRemove,
}: {
  items: string[];
  onPick: (q: string) => void;
  onRemove: (q: string) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('priceCheck.recentSearchesLabel')}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((q) => (
          <div
            key={q}
            className="flex items-center gap-1 rounded-full border bg-background pl-3 pr-1 text-xs hover:bg-muted min-h-[36px]"
          >
            <button
              type="button"
              onClick={() => onPick(q)}
              className="py-1.5 leading-none"
            >
              {q}
            </button>
            <button
              type="button"
              onClick={() => onRemove(q)}
              aria-label={t('priceCheck.removeRecentSearch')}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PriceCheckPage() {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const [input, setInput] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(null);
  // Hold the entire hit object (not just pid) so the detail view can show the
  // product image + title at the top of the offers list, since the offers
  // endpoint itself doesn't return per-offer images.
  const [selectedHit, setSelectedHit] = useState<SearchHit | null>(null);
  const selectedPid = selectedHit?.pid ?? null;
  const [recentSearches, setRecentSearches] = useState<string[]>(() => getRecentSearches());

  const search = useMutation({
    mutationFn: (q: string) => api.post<SearchResponse>('/prices/search', { q, limit: 10 }),
    onSuccess: (_data, query) => {
      setSelectedHit(null);
      setRecentSearches(pushRecentSearch(query));
    },
  });

  const offers = useQuery<OffersResponse>({
    queryKey: ['prices-by-pid', ws?.id, selectedPid],
    queryFn: () => api.get<OffersResponse>(`/prices/by-pid/${selectedPid}`),
    enabled: !!selectedPid && !!ws,
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setSubmittedQuery(q);
    search.mutate(q);
  };

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const bin = new Uint8Array(buf);
      let s = '';
      for (let i = 0; i < bin.length; i++) s += String.fromCharCode(bin[i]);
      const b64 = btoa(s);
      const r = await api.post<{ query: string | null; confidence: number; reason: string | null }>(
        '/prices/extract-from-image',
        { image_base64: b64, mime_type: f.type || 'image/jpeg' },
      );
      if (r.query) {
        setInput(r.query);
        setSubmittedQuery(r.query);
        search.mutate(r.query);
      } else {
        toast.error(t('priceCheck.cantReadImage'));
      }
    } finally {
      e.target.value = '';
    }
  };

  const cheapestOffer = offers.data?.offers
    ? [...offers.data.offers].sort((a, b) => a.price_aud - b.price_aud)[0]
    : null;

  const noKeyReason = search.data?.reason;
  const showSettingsBanner =
    noKeyReason === 'no_key' || noKeyReason === 'no_platform_key' ||
    noKeyReason === 'demo_exhausted' || noKeyReason === 'platform_pool_capped';

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header matches the Settings/Insights pattern: back button + title + subtitle */}
      <header className="border-b bg-background">
        <div className="container flex items-center gap-3 py-4">
          <BackButton to="/shopping" />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold truncate">{t('priceCheck.pageTitle')}</h1>
            <p className="text-xs text-muted-foreground line-clamp-2">{t('priceCheck.pageSubtitle')}</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-3 py-4 sm:px-4">

      {/* Search form: input full-width on top; camera + search 50/50 below (mobile-friendly tap targets) */}
      <form onSubmit={onSubmit} className="space-y-2">
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('priceCheck.placeholder')}
          enterKeyHint="search"
          className="w-full h-11 rounded-lg border bg-background px-3 text-base"
          autoFocus
        />
        <div className="grid grid-cols-2 gap-2">
          <label className="h-11 rounded-lg border bg-card flex items-center justify-center gap-1.5 text-sm font-medium cursor-pointer hover:bg-muted active:bg-muted/70 transition">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhoto}
            />
            <span aria-hidden>📷</span>
            <span>{t('priceCheck.cameraLabel')}</span>
          </label>
          <button
            type="submit"
            disabled={!input.trim() || search.isPending}
            className="h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 active:opacity-80 disabled:opacity-50 transition"
          >
            {search.isPending ? t('common.loading') : t('priceCheck.searchButton')}
          </button>
        </div>
      </form>

      {!showSettingsBanner &&
        (submittedQuery === null ||
          (search.data && search.data.hits.length === 0)) && (
          <RecentSearches
            items={recentSearches}
            onPick={(q) => {
              setInput(q);
              setSubmittedQuery(q);
              search.mutate(q);
            }}
            onRemove={(q) => setRecentSearches(removeRecentSearch(q))}
          />
        )}

      {showSettingsBanner && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-xs leading-relaxed text-yellow-900 dark:border-yellow-900/50 dark:bg-yellow-900/20 dark:text-yellow-100">
          {noKeyReason === 'demo_exhausted'
            ? t('prices.demoExhausted')
            : noKeyReason === 'platform_pool_capped'
              ? t('prices.platformPoolCapped')
              : t('prices.noKeyConfigured')}{' '}
          <Link to="/settings/integrations" className="text-primary hover:underline whitespace-nowrap">
            {t('priceCheck.openSettings')} →
          </Link>
        </div>
      )}

      {search.data && submittedQuery && search.data.hits.length === 0 && !showSettingsBanner && (
        <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
          {t('priceCheck.noResults', { query: submittedQuery })}
        </div>
      )}

      {/* Search hit list — whole card tappable, image left, title middle, price right */}
      {search.data && search.data.hits.length > 0 && (
        <motion.div
          className="space-y-2"
          variants={staggerList}
          initial="initial"
          animate="animate"
          key={submittedQuery ?? 'idle'}
        >
          {search.data.hits.map((hit) => (
            <motion.div key={hit.pid} variants={staggerItem}>
              <button
                onClick={() => setSelectedHit(hit)}
                className="w-full text-left rounded-lg border bg-card p-3 hover:bg-muted active:bg-muted/70 transition flex gap-3 items-start min-h-[5rem]"
              >
                <ProductImage src={hit.image} size="sm" />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="text-sm font-medium line-clamp-2 break-words">{hit.title}</div>
                  <div className="flex flex-wrap gap-1 items-center text-xs">
                    <StoreBadge store={hit.store} sellerRaw={hit.source} />
                    {hit.pack_size && hit.pack_size !== 'default' && (
                      <span className="text-muted-foreground">{hit.pack_size}</span>
                    )}
                    {hit.promo_pct != null && (
                      <span className="rounded bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 px-1.5 py-0.5 font-medium">
                        -{hit.promo_pct}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {hit.price != null && (
                    <div className="text-base font-semibold whitespace-nowrap">
                      {formatCurrency(hit.price, hit.currency || 'AUD')}
                    </div>
                  )}
                </div>
              </button>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Offers detail — drawer slides up over the list; vaul handles dismiss. */}
      <AppDrawer
        open={!!selectedHit}
        onOpenChange={(open) => {
          if (!open) setSelectedHit(null);
        }}
        title={offers.data?.title || selectedHit?.title || ''}
        description={
          selectedHit?.pack_size && selectedHit.pack_size !== 'default'
            ? selectedHit.pack_size
            : undefined
        }
      >
        {selectedHit && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border bg-card p-3">
              <ProductImage src={selectedHit.image} size="md" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium leading-snug break-words">
                  {offers.data?.title || selectedHit.title}
                </div>
                {selectedHit.pack_size && selectedHit.pack_size !== 'default' && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {selectedHit.pack_size}
                  </div>
                )}
              </div>
            </div>

            {offers.isLoading && (
              <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
                {t('priceCheck.fetchingOffers')}
              </div>
            )}

            {offers.data && offers.data.offers.length === 0 && (
              <div className="rounded-lg border bg-card p-3 text-sm text-muted-foreground">
                {t('priceCheck.noColesOrWoolies')}
              </div>
            )}

            {offers.data && offers.data.offers.length > 0 && (
              <div className="space-y-2">
                {offers.data.offers
                  .slice()
                  .sort((a, b) => a.price_aud - b.price_aud)
                  .map((o, idx) => {
                    const isCheapest =
                      !!cheapestOffer &&
                      o.price_aud === cheapestOffer.price_aud &&
                      o.store === cheapestOffer.store;
                    const RowEl: ElementType = o.seller_url ? 'a' : 'div';
                    return (
                      <RowEl
                        key={`${o.seller_raw}-${o.pack_size}-${idx}`}
                        href={o.seller_url ?? undefined}
                        target={o.seller_url ? '_blank' : undefined}
                        rel={o.seller_url ? 'noreferrer' : undefined}
                        className={[
                          'block rounded-lg border p-3 transition',
                          isCheapest
                            ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/10'
                            : 'bg-card',
                          o.seller_url ? 'hover:bg-muted active:bg-muted/70' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 pt-0.5">
                            <StoreBadge store={o.store} sellerRaw={o.seller_raw} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm break-words leading-snug">
                              {o.product_title || '—'}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                              <span>{o.pack_size}</span>
                              {o.seller_url && (
                                <span className="text-primary">{t('priceCheck.openSeller')} ↗</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-base font-semibold whitespace-nowrap">
                              {formatCurrency(o.price_aud, 'AUD')}
                            </div>
                            {isCheapest && (
                              <div className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                                {t('priceCheck.cheapest')}
                              </div>
                            )}
                          </div>
                        </div>
                      </RowEl>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </AppDrawer>
      </main>
    </div>
  );
}

